<template>
  <div class="map-container">
    <div class="map" ref="map"></div>
    <button
      class="map-list-button button button--secondary button--white"
      @click.prevent="$store.commit('toggleMapSideBar')"
    >
      <List class="button__icon-left" />
      {{ str('efriends.listTitle') }}
    </button>
    <CubeFilter
      class="map-filter"
      buttonClasses="button button--secondary button--white"
    />
    <MapSidebar />
  </div>
</template>

<script>
  import debounce from 'debounce'
  import Vue from 'vue'
  import MapsPopup from '@/components/efriends/MapsPopup.vue'
  import MapSidebar from '@/components/efriends/MapSidebar.vue'
  import CubeFilter from '@/components/efriends/CubeFilter'
  import List from '@/components/icons/List'
  import cubeFilterMutations from '@/store/mutations/cubeFilter'
  import { MARKER_SETTINGS } from '@/constants'

  const InfoWindowComponent = Vue.extend(MapsPopup)
  const SHOW_LABELS_ZOOM_THRESHOLD = 14
  /* global google */

  export default {
    name: 'map-efriends',
    components: {
      MapSidebar,
      CubeFilter,
      List,
    },
    data() {
      return {
        map: false,
        mapCenterChangedListender: false,
        markers: [],
        infoWindow: false,
        infoWindowComponent: false,
        unsubscribeAction: undefined,
      }
    },
    mounted() {
      const mapsJsStatus = this.$store.getters.googleMapsJavascriptStatus
      if (mapsJsStatus === 'loaded') {
        this.showMap()
      } else if (mapsJsStatus !== 'loading') {
        this.$store.dispatch('loadGoogleMaps')
      }
      this.unsubscribeAction = this.$store.subscribe((mutation) => {
        if (this.isFilterMutation(mutation.type)) this.zoomToFitResults()
      })
    },
    beforeDestroy() {
      this.map = false
      this.infoWindow = false
      this.infoWindowComponent = false
      this.unsubscribeAction()
    },
    computed: {
      mapsJsStatus: function () {
        return this.$store.getters.googleMapsJavascriptStatus
      },
      cubeList: function () {
        return this.$store.getters.allCubesByDistanceFrom({})
      },
      mapAndDataReady: function () {
        return (
          this.mapsJsStatus === 'loaded' &&
          this.$store.getters.allCubes.length > 0
        )
      },
      selectedCube: function () {
        return this.$store.getters.getCubeByName(this.$route.params.selected)
      },
    },
    methods: {
      showMap: function () {
        const selectedCube = this.$store.getters.getCubeByName(
          this.$route.params.selected
        )

        const lat =
          selectedCube?.params?.latitude ||
          this.$store.getters.homeCoordinates.lat
        const lng =
          selectedCube?.params?.longitude ||
          this.$store.getters.homeCoordinates.lng

        // This is necessary because the linter does not know we're loading
        // the google library.
        this.map = new google.maps.Map(this.$refs.map, {
          center: new google.maps.LatLng(lat, lng),
          zoom: this.$store.getters.mapsDefaultZoom,
          minZoom: 9,
          maxZoom: 21,
          zoomControlOptions: {
            position: google.maps.ControlPosition.LEFT_BOTTOM,
          },
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          styles: this.$store.getters.mapStyles,
          mapTypeControlOptions: {
            mapTypeIds: [],
          },
        })
        window.map = this.map

        this.mapCenterChangedListender = google.maps.event.addListener(
          this.map,
          'center_changed',
          debounce(this.onMapCenterChanged, 100)
        )

        this.map.addListener(
          'zoom_changed',
          debounce(this.onZoomLevelChanged, 200)
        )

        this.infoWindow = new google.maps.InfoWindow()
        // Unset the selected cube on close because otherwise opening the same
        // infowindow after closing it would not be possible.
        // Using replace here, so that the browser history is not polluted with too many pins
        google.maps.event.addListener(this.infoWindow, 'closeclick', () => {
          this.$router.replace({ name: 'map', params: { selected: null } })
        })
        this.updateMarkers()

        if (selectedCube) {
          this.openMarkerPopup(selectedCube)
        }
      },
      isFilterMutation: function (mutation) {
        return Object.keys(cubeFilterMutations).includes(mutation)
      },
      updateMarkers: function () {
        const newMarkers = this.cubeList.map((cube) => this.addMarker(cube))

        // Remove old markers to improve map performance
        const staleMarkers = this.markers.filter((marker) => {
          if (newMarkers.includes(marker)) return false
          if (
            this.selectedCube &&
            marker.cubeName === this.selectedCube.cubeName
          )
            return false
          return true
        })
        this.deleteMarkers(staleMarkers)

        this.markers = newMarkers
      },
      addMarker(cube) {
        if (!cube) return
        // Don't add multiple markers for a single cube.
        const existingMarker = this.getMarkerForCube(cube)
        if (existingMarker) return existingMarker

        const marker = new google.maps.Marker({
          position: cube.mapPosition,
          map: this.map,
          icon: this.getCubeMarkerIcon(cube),
          cubeName: cube.params.cubeName,
          // Turn off optimized grouping in canvas elements, since that intereferes with z-index ordering
          // See: https://stackoverflow.com/questions/11845916/google-maps-marker-zindex-doesnt-work-for-two-icon-types-symbol-and-string
          optimized: false,
          zIndex: cube.hasProduction || cube.canReceiveDonations ? 10 : 1,
          originalLabel: this.getCubeMarkerTitle(cube),
        })

        if (this.map.getZoom() >= SHOW_LABELS_ZOOM_THRESHOLD) {
          marker.setLabel(marker.originalLabel)
        }

        if (this.cubeIsClickable(cube)) {
          marker.addListener(
            'click',
            this.onMarkerClick.bind(this, marker, cube)
          )
        }
        return marker
      },
      zoomToFitResults() {
        const markerBounds = new google.maps.LatLngBounds()

        if (this.markers.length === 0) return

        for (let marker of this.markers) {
          markerBounds.extend(marker.getPosition())
        }

        this.map.fitBounds(markerBounds)
      },
      cubeIsClickable(cube) {
        if (cube.hasProduction || cube.canReceiveDonations) {
          return true
        }

        const imgDetails = cube.params.infoWindowImageDetails
        const cubeText = cube.params.infoWindowText
        if (
          cubeText ||
          (imgDetails && imgDetails.original && imgDetails.original.url)
        ) {
          return true
        }

        return false
      },
      onMarkerClick(marker, cube) {
        // Using replace here, so that the browser history is not polluted with too many pins
        this.$router.replace({
          name: 'map',
          params: { selected: cube.params.cubeName },
        })
      },
      openMarkerPopup(cube) {
        this.map.panTo(
          new google.maps.LatLng(cube.params.latitude, cube.params.longitude)
        )

        const marker = this.getMarkerForCube(cube) || this.addMarker(cube)

        if (!this.cubeIsClickable(cube)) {
          return
        }

        if (this.infoWindowComponent) {
          this.infoWindowComponent.$destroy()
        }

        // We manually create a vue instance that we mount, but not into the dom.
        // This element can then be used as content for the info window.
        // Note that we needed to extend the original vue component (see top of
        // this file). If we try to create instances of the original component
        // there's a js error (at least in dev environment - vue cli dev).
        // So we do it like in the example from
        // https://css-tricks.com/creating-vue-js-component-instances-programmatically/
        this.infoWindowComponent = new InfoWindowComponent({
          propsData: {
            cube: cube,
            store: this.$store,
          },
        })
        this.infoWindowComponent.$mount()
        this.infoWindow.setContent(this.infoWindowComponent.$el)
        this.infoWindow.open(this.map, marker)
      },
      onMapCenterChanged() {
        // Store the new center.
        const center = this.map.getCenter()
        this.$store.commit('mapCenterChanged', {
          latitude: center.lat(),
          longitude: center.lng(),
        })
      },
      getCubeMarkerTitle(rawCube) {
        const cube = rawCube.params
        const isMe = this.cubeIsMe(cube)
        let result = cube.nickName

        if (rawCube.hasProduction) {
          if (
            cube.openConnectionsAvailable &&
            cube.openConnectionsAvailable > 0
          ) {
            result += ` (${cube.openConnectionsAvailable})`
          }
          return result
        } else if (rawCube.canReceiveDonations || isMe) {
          return result
        } else if (
          cube.infoWindowText ||
          (cube.infoWindowImageDetails && cube.infoWindowImageDetails.original)
        ) {
          return result
        } else {
          return ''
        }
      },
      onZoomLevelChanged() {
        const zoomLevel = this.map.getZoom()

        this.markers.map((marker) => {
          if (zoomLevel >= SHOW_LABELS_ZOOM_THRESHOLD && !marker.getLabel()) {
            marker.setLabel(marker.originalLabel)
          } else if (
            zoomLevel < SHOW_LABELS_ZOOM_THRESHOLD &&
            marker.getLabel()
          ) {
            marker.setLabel(null)
          }
        })
      },
      cubeIsMe(cube) {
        return cube.nickName === this.$store.getters.nickName
      },
      hasPinIcon(cube) {
        return !!cube.params.productionType
      },
      getCubeMarkerIcon(cube) {
        const baseSettings = {
          url: this.$store.getters.cubeIconUrl(cube, 'png'),
          origin: new google.maps.Point(0, 0),
        }

        if (this.hasPinIcon(cube)) {
          return {
            ...baseSettings,
            scaledSize: new google.maps.Size(
              MARKER_SETTINGS.producer.width,
              MARKER_SETTINGS.producer.height
            ),
            anchor: new google.maps.Point(
              MARKER_SETTINGS.producer.anchor.x,
              MARKER_SETTINGS.producer.anchor.y
            ),
            labelOrigin: new google.maps.Point(
              MARKER_SETTINGS.producer.anchor.x,
              MARKER_SETTINGS.producer.height +
                MARKER_SETTINGS.producer.labelOffset
            ),
          }
        }

        // Consumer Icon
        return {
          ...baseSettings,
          scaledSize: new google.maps.Size(
            MARKER_SETTINGS.consumer.width,
            MARKER_SETTINGS.consumer.height
          ),
          anchor: new google.maps.Point(
            MARKER_SETTINGS.consumer.anchor.x,
            MARKER_SETTINGS.consumer.anchor.y
          ),
          labelOrigin: new google.maps.Point(
            MARKER_SETTINGS.consumer.anchor.x,
            MARKER_SETTINGS.consumer.height +
              MARKER_SETTINGS.producer.labelOffset
          ),
        }
      },
      getMarkerForCube(cube) {
        return this.markers.find(
          (marker) => marker.cubeName === cube.params.cubeName
        )
      },
      deleteMarkers(markersToDelete) {
        for (let marker of markersToDelete) {
          if (marker.setMap) marker.setMap(null)
        }
      },
    },
    watch: {
      mapAndDataReady: function (ready) {
        if (ready && !this.map) {
          this.showMap()
        }
      },
      cubeList: function (cubeList) {
        if (cubeList && this.map) {
          this.updateMarkers()
        }
      },
      selectedCube: function (newCube) {
        if (newCube) {
          this.openMarkerPopup(newCube)
        }
      },
    },
  }
</script>

<style lang="scss">
  $map-dropshadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;

  .map-container {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    transition: $transition-base;
    overflow-x: hidden;

    .map {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      transition: $transition-base;

      @media (min-width: $bp-lg) {
        width: 100% - $map-sidebar-width-lg;
      }
    }

    > .map-filter {
      position: absolute;
      top: spacer(1);
      left: 50%;
      max-width: 90vw;
      align-items: center;
      transform: translateX(-50%);

      > * {
        box-shadow: $map-dropshadow;
        background-color: $white;
      }

      @media (min-width: $bp-lg) {
        display: none;
      }
    }
  }

  .map-list-button {
    position: absolute;
    bottom: 2.2rem;
    right: 0.8rem;
    box-shadow: $map-dropshadow;

    @media (min-width: $bp-lg) {
      display: none;
    }
  }
</style>
